import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Auth, Hub } from "aws-amplify";
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  LuArrowRight,
  LuCheck,
  LuLock,
  LuMenu,
  LuX,
  LuSearch,
  LuMonitor,
  LuSmartphone,
  LuShieldCheck,
  LuSun,
  LuMoon,
} from "react-icons/lu";
import { useTheme } from "../context/ThemeContext.js";
import {
  clearReturnPath,
  getPostLoginPath,
  readReturnPath,
} from "../utils/postLoginPath";
import markLogo from "../assets/images/no-text-logo.svg";
import HeroMockup from "../components/landing/HeroMockup";
import LoginDialog from "../components/landing/LoginDialog";
import { FEATURES, SHIFT_FLOW, GLOSSARY } from "../components/landing/content";

// Set REACT_APP_DEMO_EMAIL to show the "Book a demo" call to action.
const DEMO_EMAIL = process.env.REACT_APP_DEMO_EMAIL;
const DEMO_HREF = DEMO_EMAIL
  ? `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent("Care Connect demo request")}`
  : null;

const NAV_LINKS = [
  { href: "#features", label: "Features" },
  { href: "#how-it-works", label: "How it works" },
  { href: "#who-its-for", label: "Who it's for" },
  { href: "#glossary", label: "Glossary" },
];

const ROLES = [
  {
    icon: LuMonitor,
    title: "Care managers & office teams",
    body: "Run rotas, service user records, medication and templates from the web dashboard.",
  },
  {
    icon: LuSmartphone,
    title: "Field carers",
    body: "See today's shifts, clock in and out, add care notes and record medication on the go.",
  },
  {
    icon: LuShieldCheck,
    title: "Administrators",
    body: "Create accounts and control who sees what with role-based access.",
  },
];

function Brand() {
  return (
    <a href="#top" className="lp-brand" aria-label="Care Connect Software home">
      <img src={markLogo} alt="" className="lp-brand__mark" />
      <span className="lp-brand__text">
        <span className="lp-brand__name">CARECONNECT</span>
        <span className="lp-brand__sub">SOFTWARE</span>
        <span className="lp-brand__tag">Technology that cares</span>
      </span>
    </a>
  );
}

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();

  // Open the sign-in dialog straight away on /login, or when the user was
  // bounced here from a protected page.
  const [loginOpen, setLoginOpen] = useState(
    () =>
      location.pathname === "/login" ||
      Boolean(location.state?.from) ||
      Boolean(readReturnPath())
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [glossaryQuery, setGlossaryQuery] = useState("");
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const signInHandled = useRef(false);
  // The protected page the user was bounced from — router state for an in-app
  // redirect, or the saved path after a forced sign-out reload. Captured once
  // so the sign-in handler (registered on mount) always sees it.
  // Cleared only once used (sign-in) or dismissed (dialog closed): a failed
  // API call can still reload this page before the user signs in.
  const redirectFrom = useRef(location.state?.from || readReturnPath());

  useEffect(() => {
    const listener = (data) => {
      const { event } = data.payload;

      if (event === "signIn") {
        handleSignIn();
      }
    };

    // Hub.listen returns its own unsubscribe (Hub.remove is deprecated)
    const stopListening = Hub.listen("auth", listener);

    return () => stopListening(); // Cleanup on unmount
  }, []);

  // A Cognito session with no stored user (e.g. site storage partly cleared)
  // would leave the Authenticator rendering nothing. Rebuild the user instead.
  useEffect(() => {
    if (authStatus === "authenticated" && !localStorage.getItem("user")) {
      handleSignIn();
    }
  }, [authStatus]);

  const handleSignIn = async () => {
    // Hub "signIn" and the authStatus change can both fire for one login
    if (signInHandled.current) return;
    signInHandled.current = true;

    try {
      const loginData = await Auth.currentAuthenticatedUser();
      const groups =
        loginData.signInUserSession.accessToken.payload["cognito:groups"] || [];
      const token = loginData.signInUserSession.idToken.jwtToken;
      const uuid = loginData.signInUserSession.idToken.payload.sub;

      const user = {
        username: loginData.username,
        email: loginData.attributes.email,
        firstName: loginData.attributes.given_name,
        lastName: loginData.attributes.family_name,
        jwt: token,
        id: loginData.id || uuid,
        userGroup: groups,
      };

      localStorage.setItem("user", JSON.stringify(user));
      clearReturnPath();
      window.location.href = getPostLoginPath(groups, redirectFrom.current);
    } catch (error) {
      console.error("Error fetching user or token:", error);
      signInHandled.current = false;
    }
  };

  const openLogin = () => {
    setMenuOpen(false);
    setLoginOpen(true);
  };

  const closeLogin = useCallback(() => {
    setLoginOpen(false);
    clearReturnPath();
    redirectFrom.current = null;
    // Drop /login and any redirect state so a refresh doesn't reopen the dialog
    if (location.pathname === "/login" || location.state?.from) {
      navigate("/", { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const filteredGlossary = useMemo(() => {
    const q = glossaryQuery.trim().toLowerCase();
    if (!q) return GLOSSARY;
    return GLOSSARY.filter(
      (g) => g.term.toLowerCase().includes(q) || g.def.toLowerCase().includes(q)
    );
  }, [glossaryQuery]);

  const year = new Date().getFullYear();

  return (
    <div className="landing" id="top">
      <header className={"lp-header" + (menuOpen ? " is-open" : "")}>
        <div className="lp-container lp-header__inner">
          <Brand />

          <nav className="lp-nav" aria-label="Main">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}>
                {l.label}
              </a>
            ))}
          </nav>

          <div className="lp-header__actions">
            {DEMO_HREF && (
              <a href={DEMO_HREF} className="lp-btn lp-btn--ghost lp-btn--sm">
                Book a demo
              </a>
            )}
            <button
              type="button"
              className="lp-theme-toggle"
              onClick={() => toggleTheme(!isDark)}
              aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
              title={isDark ? "Light theme" : "Dark theme"}
            >
              {isDark ? <LuSun /> : <LuMoon />}
              {/* Label only shows inside the mobile menu */}
              <span className="lp-theme-toggle__label" aria-hidden="true">
                {isDark ? "Light theme" : "Dark theme"}
              </span>
            </button>
          </div>

          {/* The single sign-in entry point — stays in the top bar on every screen size */}
          <button
            type="button"
            className="lp-btn lp-btn--outline lp-btn--sm lp-header__login"
            onClick={openLogin}
          >
            {/* The visible label is the accessible name (WCAG 2.5.3) */}
            <LuLock aria-hidden="true" />
            <span className="lp-header__login-text--full">Client login</span>
            <span className="lp-header__login-text--short">Log in</span>
          </button>

          <button
            type="button"
            className="lp-header__toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? <LuX /> : <LuMenu />}
          </button>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="lp-hero">
          <div className="lp-hero__glow" aria-hidden="true" />
          <div className="lp-container lp-hero__inner">
            <div className="lp-hero__copy">
              <h1>
                All-in-one care management <span className="lp-gradient-text">software</span>
              </h1>
              <p className="lp-hero__strap">Simpler operations. Happier staff. Better care.</p>
              <p className="lp-hero__lede">
                Care Connect helps care providers manage service users, staff, rotas, medication
                and care records — from the office dashboard to the carer's phone, on one secure
                platform.
              </p>

              <div className="lp-hero__ctas">
                {DEMO_HREF ? (
                  <a href={DEMO_HREF} className="lp-btn lp-btn--primary">
                    Book a demo <LuArrowRight />
                  </a>
                ) : (
                  <a href="#features" className="lp-btn lp-btn--primary">
                    Explore features <LuArrowRight />
                  </a>
                )}
                <a href="#how-it-works" className="lp-btn lp-btn--outline">
                  How it works
                </a>
              </div>

              <ul className="lp-hero__checks">
                <li><span><LuCheck /></span>Secure sign-in</li>
                <li><span><LuCheck /></span>Web &amp; mobile</li>
                <li><span><LuCheck /></span>Built for care providers</li>
              </ul>
            </div>

            <div className="lp-hero__visual">
              <HeroMockup />
              <p className="lp-script lp-script--hero" aria-hidden="true">
                Care in their hands.
                <br />
                Anywhere.
              </p>
            </div>
          </div>
        </section>

        {/* Who it's for */}
        <section className="lp-roles" id="who-its-for">
          <div className="lp-container">
            <p className="lp-eyebrow">One platform, every role</p>
            <div className="lp-roles__grid">
              {ROLES.map(({ icon: Icon, title, body }) => (
                <div key={title} className="lp-role">
                  <span className="lp-role__icon"><Icon /></span>
                  <div>
                    <h3>{title}</h3>
                    <p>{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="lp-section" id="features">
          <div className="lp-container">
            <div className="lp-section__head">
              <h2>Everything you need to run a modern care service</h2>
              <p>
                A complete platform built around your people, your service users and the care you
                deliver every day.
              </p>
            </div>

            <div className="lp-features">
              {FEATURES.map(({ icon: Icon, tone, title, body }) => (
                <article key={title} className="lp-feature">
                  <span className={`lp-feature__icon lp-feature__icon--${tone}`}>
                    <Icon />
                  </span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </article>
              ))}
            </div>

            <p className="lp-script lp-script--features" aria-hidden="true">
              Technology that cares
            </p>
          </div>
        </section>

        {/* How a shift flows */}
        <section className="lp-section lp-section--tinted" id="how-it-works">
          <div className="lp-container">
            <div className="lp-section__head">
              <p className="lp-eyebrow">How it works</p>
              <h2>From the office to the doorstep</h2>
              <p>
                The web dashboard and the carer's mobile app share the same records, so every
                shift is visible from planning to completion.
              </p>
            </div>

            <ol className="lp-flow">
              {SHIFT_FLOW.map((s) => (
                <li key={s.step} className="lp-flow__step">
                  <span className="lp-flow__num">{s.step}</span>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Glossary */}
        <section className="lp-section" id="glossary">
          <div className="lp-container">
            <div className="lp-section__head lp-section__head--split">
              <div>
                <p className="lp-eyebrow">Glossary</p>
                <h2>Speak the same language</h2>
                <p>The terms you'll see across Care Connect, on the web and in the app.</p>
              </div>
              <label className="lp-search">
                <LuSearch aria-hidden="true" />
                <input
                  type="search"
                  placeholder="Search terms"
                  value={glossaryQuery}
                  onChange={(e) => setGlossaryQuery(e.target.value)}
                  aria-label="Search glossary"
                />
              </label>
            </div>

            {filteredGlossary.length > 0 ? (
              <dl className="lp-glossary">
                {filteredGlossary.map((g) => (
                  <div key={g.term} className="lp-glossary__item">
                    <dt>{g.term}</dt>
                    <dd>{g.def}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="lp-glossary__empty">
                No terms match “{glossaryQuery.trim()}”.
              </p>
            )}
          </div>
        </section>

        {/* Closing CTA — sign-in lives only in the header, so this band is for demos */}
        {DEMO_HREF && (
          <section className="lp-cta">
            <div className="lp-container lp-cta__inner">
              <div>
                <h2>See Care Connect in action</h2>
                <p>Book a short demo of rotas, eMAR and the carer app with our team.</p>
              </div>
              <div className="lp-cta__actions">
                <a href={DEMO_HREF} className="lp-btn lp-btn--light">
                  Book a demo <LuArrowRight />
                </a>
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="lp-footer">
        <div className="lp-container lp-footer__inner">
          <Brand />
          <nav className="lp-footer__links" aria-label="Footer">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href}>{l.label}</a>
            ))}
          </nav>
          <p className="lp-footer__copy">© {year} Care Connect Software</p>
        </div>
      </footer>

      <LoginDialog open={loginOpen} onClose={closeLogin} />
    </div>
  );
}
